import { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router";
import { toast } from "@cliently/ui";
import { useAuthV2 } from "@cliently/auth-client";
import { api } from "../lib/api";

/**
 * Équivalent auth-v2 de `PortalGuard`. Vérifie que l'utilisateur connecté est
 * bien lié à un client (et non à un compte pro). En cas de mismatch, déconnecte
 * proprement après 2.5 s.
 */
export const PortalGuardV2 = () => {
  const { isSignedIn, isLoaded, signOut } = useAuthV2();
  const [verified, setVerified] = useState<"loading" | "ok" | "forbidden">(
    "loading",
  );

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;

    let cancelled = false;
    api
      .get("/portal/me")
      .then(() => {
        if (!cancelled) setVerified("ok");
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        const msg: string = err?.response?.data?.message ?? "";

        if (status === 403) {
          setVerified("forbidden");
          if (msg.includes("compte professionnel")) {
            toast.error(
              "Ce compte est un compte professionnel. Redirection...",
            );
          } else {
            toast.error(
              "Votre compte n'est pas encore relié à un praticien. Veuillez utiliser le lien et le code d'accès reçus pour activer votre espace.",
            );
          }
          setTimeout(() => void signOut(), 2500);
        } else {
          setVerified("ok");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, signOut]);

  if (!isLoaded || (isSignedIn && verified === "loading")) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }
  if (!isSignedIn) return <Navigate to="/" replace />;
  if (verified === "forbidden") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <p className="text-destructive text-lg font-medium">
          Ce compte est un compte professionnel.
        </p>
        <p className="text-muted-foreground">Déconnexion en cours...</p>
      </div>
    );
  }
  return <Outlet />;
};
